// react_frontend/src/Components/Modules/CompanySetting.js
import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import Breadcrumb from "../Partials/Breadcrumb";
import { AxiosAuthInstance } from "../../AxiosInterceptors";
import { MotherContext } from "../../Context/MotherContext";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import {
  FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt, FaIdCard,
  FaPercent, FaDollarSign, FaImage, FaUpload, FaCheckCircle,
  FaClock, FaCalendarAlt, FaMoneyBillWave, FaBox, FaFileInvoice,
  FaUserShield, FaPalette, FaSave, FaTimes, FaChartLine,
  FaCog, FaBell, FaClipboardList, FaUsers, FaSignOutAlt, FaGift,
  FaBriefcase, FaShieldAlt, FaStar, FaCrown, FaEdit, FaTrash, FaPlus, FaMinus, FaMapMarker,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";
import Logo from "../../Assets/Media/Logo.png";
import FestivalManagement from "../Modules/CompanySetting/FestivalManagement";
import EmploymentTypeRules from "../Modules/CompanySetting/EmploymentTypeRules";

const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "400px",
    height: "400px",
    background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
    borderRadius: "50%",
    transform: "translate(30%, -30%)",
  },
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: "white",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  marginBottom: theme.spacing(3),
}));

const TabButton = styled(Button)(({ theme, active }) => ({
  justifyContent: "flex-start",
  textTransform: "none",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(0.5),
  width: "100%",
  background: active ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
  color: active ? "white" : "#666",
  fontWeight: active ? 600 : 500,
  transition: "all 0.3s ease",
  "&:hover": {
    background: active
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "rgba(102, 126, 234, 0.08)",
    transform: "translateX(4px)",
  },
}));

const ToggleCard = styled(Paper)(({ theme, active }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: active ? "2px solid #667eea" : "1px solid rgba(0,0,0,0.08)",
  background: active ? "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)" : "white",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "#667eea",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
  },
}));

const LogoUploadArea = styled(Box)(({ theme }) => ({
  border: "2px dashed #667eea",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  background: "rgba(102, 126, 234, 0.02)",
  "&:hover": {
    background: "rgba(102, 126, 234, 0.05)",
    borderColor: "#764ba2",
  },
}));

const CompanySetting = () => {
  const { updateCompany, fetchProfile } = useContext(MotherContext);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(Logo);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  const [workstations, setWorkstations] = useState([]);
  const [wsForm, setWsForm] = useState({ name: "", code: "", type: "office", address: "" });
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

  const [businessRules, setBusinessRules] = useState({
    salary_rules: {
      basic_percent: 0, house_rent_percent: 0, medical_percent: 0,
      conveyance_percent: 0, food_allowance_percent: 0,
      transport_allowance_percent: 0, mobile_allowance_percent: 0,
      overtime_rate: 0, working_days_per_month: 0, daily_working_hours: 0,
    },
    office_start_time: "", office_end_time: "",
    grace_period_minutes: 0, late_penalty_minutes: 0,
    enable_gps_attendance: false, gps_radius_km: 0,
    enable_self_attendance: false, enable_ip_restriction: false, allowed_ips: "",
    leave_policies: { casual: 0, sick: 0, annual: 0, maternity: 0, paternity: 0, unpaid: 0 },
    leave_encashment: false, max_leave_carry_forward: 0, leave_approval_required: false,
    tax_rate: 0, enable_advance_salary: false, max_advance_percentage: 0,
    enable_loan: false, max_loan_amount: 0,
    enable_negative_stock: false, auto_stock_alert: false, low_stock_threshold: 0,
    enable_batch_tracking: false, enable_expiry_tracking: false,
    enable_invoice_numbering: false, invoice_prefix: "", invoice_digit_length: 0,
    auto_send_invoice_email: false, enable_discount: false, max_discount_percentage: 0,
    enable_tax_in_invoice: false,
    enable_customer_credit: false, max_credit_limit: 0, credit_days: 0,
    enable_loyalty_points: false, loyalty_point_percentage: 0,
    approval_levels: 0, approval_hierarchy: {},
    enable_email_notifications: false, enable_sms_notifications: false,
    enable_push_notifications: false, birthday_notification: false,
    attendance_alert: false, stock_alert: false,
    password_expiry_days: 0, enable_two_factor: false,
    session_timeout_minutes: 0, enable_ip_whitelist: false,
    footer_text: "", terms_conditions: "", company_tagline: "", enable_logo_on_invoice: false,
    employee_increment_rules: {
      annual_percent: 10, performance_max: 20, tenure_bonus: 5, min_service_months: 12,
    },
    termination_rules: {
      notice_days: 30, severance_months: 1, leave_encashment: true, max_years_for_severance: 10,
    },
    resignation_rules: { notice_days: 30, leave_encashment: true, bonus_prorated: true },
    probation_rules: { duration_months: 3, confirmation_increment: 5 },
  });

  const currencyList = [
    { code: "BDT", symbol: "৳" }, { code: "USD", symbol: "$" },
    { code: "EUR", symbol: "€" }, { code: "GBP", symbol: "£" },
    { code: "INR", symbol: "₹" }, { code: "AED", symbol: "د.إ" },
    { code: "SAR", symbol: "ر.س" },
  ].sort((a, b) => a.code.localeCompare(b.code));

  const tabs = [
    { key: "general", label: "General", icon: FaBuilding },
    { key: "salary", label: "Salary", icon: FaMoneyBillWave },
    { key: "attendance", label: "Attendance", icon: FaClock },
    { key: "leave", label: "Leave", icon: FaCalendarAlt },
    { key: "financial", label: "Financial", icon: FaChartLine },
    { key: "inventory", label: "Inventory", icon: FaBox },
    { key: "billing", label: "Billing", icon: FaFileInvoice },
    { key: "customer", label: "Customer", icon: FaUserShield },
    { key: "approval", label: "Approval", icon: FaClipboardList },
    { key: "notification", label: "Notifications", icon: FaBell },
    { key: "security", label: "Security", icon: FaShieldAlt },
    { key: "branding", label: "Branding", icon: FaPalette },
    { key: "employee", label: "Employee Rules", icon: FaUsers },
    { key: "separation", label: "Separation", icon: FaSignOutAlt },
    { key: "festivals", label: "Festival Bonus", icon: FaGift },
    { key: "employment", label: "Employment", icon: FaBriefcase },
    { key: "workstation", label: "Workstation", icon: FaMapMarkerAlt },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const loadProfile = useCallback(async () => {
    try {
      const fetchedCompany = await fetchProfile();
      if (fetchedCompany) {
        setData({
          name: fetchedCompany.name || "", email: fetchedCompany.email || "",
          phone: fetchedCompany.phone || "", address: fetchedCompany.address || "",
          mushak_number: fetchedCompany.mushak_number || "", vat: fetchedCompany.vat || 0,
          currency: fetchedCompany.currency || "BDT", point: fetchedCompany.point || 0,
          logo: fetchedCompany.logo || "", package: fetchedCompany.package || "Trial",
          is_trial_active: fetchedCompany.is_trial_active || false,
        });
        if (fetchedCompany.logo) {
          const cleanPath = fetchedCompany.logo.replace(/\\/g, "/").split(" ").join("%20");
          setPreviewUrl(`${apiBaseUrl}/${cleanPath}`);
        }
      }
      try {
        const res = await AxiosAuthInstance.get("company/business-rules");
        if (res.data?.status && res.data?.rules) {
          const rules = res.data.rules;
          setBusinessRules(prev => ({
            ...prev,
            salary_rules: { ...prev.salary_rules, ...(rules.salary_rules || {}) },
            office_start_time: rules.office_start_time || "",
            office_end_time: rules.office_end_time || "",
            grace_period_minutes: rules.grace_period_minutes ?? 0,
            late_penalty_minutes: rules.late_penalty_minutes ?? 0,
            enable_gps_attendance: rules.enable_gps_attendance ?? false,
            gps_radius_km: rules.gps_radius_km ?? 0,
            enable_self_attendance: rules.enable_self_attendance ?? false,
            enable_ip_restriction: rules.enable_ip_restriction ?? false,
            allowed_ips: rules.allowed_ips || "",
            leave_policies: { ...prev.leave_policies, ...(rules.leave_policies || {}) },
            leave_encashment: rules.leave_encashment ?? false,
            max_leave_carry_forward: rules.max_leave_carry_forward ?? 0,
            leave_approval_required: rules.leave_approval_required ?? false,
            tax_rate: rules.tax_rate ?? 0,
            enable_advance_salary: rules.enable_advance_salary ?? false,
            max_advance_percentage: rules.max_advance_percentage ?? 0,
            enable_loan: rules.enable_loan ?? false,
            max_loan_amount: rules.max_loan_amount ?? 0,
            enable_negative_stock: rules.enable_negative_stock ?? false,
            auto_stock_alert: rules.auto_stock_alert ?? false,
            low_stock_threshold: rules.low_stock_threshold ?? 0,
            enable_batch_tracking: rules.enable_batch_tracking ?? false,
            enable_expiry_tracking: rules.enable_expiry_tracking ?? false,
            enable_invoice_numbering: rules.enable_invoice_numbering ?? false,
            invoice_prefix: rules.invoice_prefix || "",
            invoice_digit_length: rules.invoice_digit_length ?? 0,
            auto_send_invoice_email: rules.auto_send_invoice_email ?? false,
            enable_discount: rules.enable_discount ?? false,
            max_discount_percentage: rules.max_discount_percentage ?? 0,
            enable_tax_in_invoice: rules.enable_tax_in_invoice ?? false,
            enable_customer_credit: rules.enable_customer_credit ?? false,
            max_credit_limit: rules.max_credit_limit ?? 0,
            credit_days: rules.credit_days ?? 0,
            enable_loyalty_points: rules.enable_loyalty_points ?? false,
            loyalty_point_percentage: rules.loyalty_point_percentage ?? 0,
            approval_levels: rules.approval_levels ?? 0,
            approval_hierarchy: rules.approval_hierarchy || {},
            enable_email_notifications: rules.enable_email_notifications ?? false,
            enable_sms_notifications: rules.enable_sms_notifications ?? false,
            enable_push_notifications: rules.enable_push_notifications ?? false,
            birthday_notification: rules.birthday_notification ?? false,
            attendance_alert: rules.attendance_alert ?? false,
            stock_alert: rules.stock_alert ?? false,
            password_expiry_days: rules.password_expiry_days ?? 0,
            enable_two_factor: rules.enable_two_factor ?? false,
            session_timeout_minutes: rules.session_timeout_minutes ?? 0,
            enable_ip_whitelist: rules.enable_ip_whitelist ?? false,
            footer_text: rules.footer_text || "",
            terms_conditions: rules.terms_conditions || "",
            company_tagline: rules.company_tagline || "",
            enable_logo_on_invoice: rules.enable_logo_on_invoice ?? false,
          }));
        }
      } catch (err) {
        console.warn("Business rules endpoint not available");
      }
    } catch (error) {
      console.error("Failed to load profile", error);
      showSnackbar("Failed to load company profile", "error");
    }
  }, [fetchProfile, apiBaseUrl]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  useEffect(() => {
    AxiosAuthInstance.get("workstations").then(res => setWorkstations(res.data)).catch(() => { });
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2097152) {
        showSnackbar("File size must be less than 2MB", "error");
        return;
      }
      setData((prev) => ({ ...prev, logo: file }));
      setPreviewUrl(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, company_logo: null }));
    }
  };

  const handleNestedChange = (category, key, value) => {
    setBusinessRules(prev => {
      const currentCategory = prev[category];
      if (typeof currentCategory === 'object' && currentCategory !== null) {
        return { ...prev, [category]: { ...currentCategory, [key]: value } };
      }
      return prev;
    });
  };

  const handleSimpleChange = (key, value) => {
    setBusinessRules(prev => ({ ...prev, [key]: value }));
  };

  const formatTimeForBackend = (timeValue) => {
    if (!timeValue) return null;
    const match = timeValue.match(/^(\d{2}):(\d{2})/);
    if (match) return `${match[1]}:${match[2]}`;
    return null;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});
    const formData = new FormData();

    formData.append("company_name", data.name?.trim() || "");
    formData.append("company_email", data.email?.trim() || "");
    formData.append("company_phone", data.phone?.trim() || "");
    formData.append("company_address", data.address?.trim() || "");
    formData.append("mushak_number", data.mushak_number?.trim() || "");
    formData.append("vat", data.vat ?? 0);
    formData.append("currency", data.currency || "BDT");
    formData.append("point", data.point ?? 0);

    Object.entries(businessRules.salary_rules).forEach(([key, value]) => {
      formData.append(`salary_rules[${key}]`, value ?? 0);
    });

    formData.append("office_start_time", formatTimeForBackend(businessRules.office_start_time) || "");
    formData.append("office_end_time", formatTimeForBackend(businessRules.office_end_time) || "");
    formData.append("grace_period_minutes", businessRules.grace_period_minutes ?? 0);
    formData.append("late_penalty_minutes", businessRules.late_penalty_minutes ?? 0);
    formData.append("enable_gps_attendance", businessRules.enable_gps_attendance ? 1 : 0);
    formData.append("gps_radius_km", businessRules.gps_radius_km ?? 0);
    formData.append("enable_self_attendance", businessRules.enable_self_attendance ? 1 : 0);
    formData.append("enable_ip_restriction", businessRules.enable_ip_restriction ? 1 : 0);
    formData.append("allowed_ips", businessRules.allowed_ips || "");

    Object.entries(businessRules.leave_policies).forEach(([key, value]) => {
      formData.append(`leave_policies[${key}]`, value ?? 0);
    });
    formData.append("leave_encashment", businessRules.leave_encashment ? 1 : 0);
    formData.append("max_leave_carry_forward", businessRules.max_leave_carry_forward ?? 0);
    formData.append("leave_approval_required", businessRules.leave_approval_required ? 1 : 0);

    formData.append("tax_rate", businessRules.tax_rate ?? 0);
    formData.append("enable_advance_salary", businessRules.enable_advance_salary ? 1 : 0);
    formData.append("max_advance_percentage", businessRules.max_advance_percentage ?? 0);
    formData.append("enable_loan", businessRules.enable_loan ? 1 : 0);
    formData.append("max_loan_amount", businessRules.max_loan_amount ?? 0);

    formData.append("enable_negative_stock", businessRules.enable_negative_stock ? 1 : 0);
    formData.append("auto_stock_alert", businessRules.auto_stock_alert ? 1 : 0);
    formData.append("low_stock_threshold", businessRules.low_stock_threshold ?? 0);
    formData.append("enable_batch_tracking", businessRules.enable_batch_tracking ? 1 : 0);
    formData.append("enable_expiry_tracking", businessRules.enable_expiry_tracking ? 1 : 0);

    formData.append("enable_invoice_numbering", businessRules.enable_invoice_numbering ? 1 : 0);
    formData.append("invoice_prefix", businessRules.invoice_prefix?.trim() || "");
    formData.append("invoice_digit_length", businessRules.invoice_digit_length ?? 0);
    formData.append("auto_send_invoice_email", businessRules.auto_send_invoice_email ? 1 : 0);
    formData.append("enable_discount", businessRules.enable_discount ? 1 : 0);
    formData.append("max_discount_percentage", businessRules.max_discount_percentage ?? 0);
    formData.append("enable_tax_in_invoice", businessRules.enable_tax_in_invoice ? 1 : 0);

    formData.append("enable_customer_credit", businessRules.enable_customer_credit ? 1 : 0);
    formData.append("max_credit_limit", businessRules.max_credit_limit ?? 0);
    formData.append("credit_days", businessRules.credit_days ?? 0);
    formData.append("enable_loyalty_points", businessRules.enable_loyalty_points ? 1 : 0);
    formData.append("loyalty_point_percentage", businessRules.loyalty_point_percentage ?? 0);

    formData.append("approval_levels", businessRules.approval_levels ?? 0);
    formData.append("enable_email_notifications", businessRules.enable_email_notifications ? 1 : 0);
    formData.append("enable_sms_notifications", businessRules.enable_sms_notifications ? 1 : 0);
    formData.append("enable_push_notifications", businessRules.enable_push_notifications ? 1 : 0);
    formData.append("birthday_notification", businessRules.birthday_notification ? 1 : 0);
    formData.append("attendance_alert", businessRules.attendance_alert ? 1 : 0);
    formData.append("stock_alert", businessRules.stock_alert ? 1 : 0);

    formData.append("password_expiry_days", businessRules.password_expiry_days ?? 0);
    formData.append("enable_two_factor", businessRules.enable_two_factor ? 1 : 0);
    formData.append("session_timeout_minutes", businessRules.session_timeout_minutes ?? 0);
    formData.append("enable_ip_whitelist", businessRules.enable_ip_whitelist ? 1 : 0);

    formData.append("footer_text", businessRules.footer_text?.trim() || "");
    formData.append("terms_conditions", businessRules.terms_conditions?.trim() || "");
    formData.append("company_tagline", businessRules.company_tagline?.trim() || "");

    if (data.logo && (data.logo instanceof File || data.logo instanceof Blob)) {
      formData.append("company_logo", data.logo);
    }

    try {
      const response = await AxiosAuthInstance.post("company/profile/update", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data?.status === true) {
        const updatedCompany = {
          name: response.data.company?.company_name || data.name,
          email: response.data.company?.company_email || data.email,
          phone: response.data.company?.company_phone || data.phone,
          address: response.data.company?.company_address || data.address,
          mushak_number: response.data.company?.mushak_number || data.mushak_number,
          vat: response.data.company?.vat ?? data.vat,
          currency: response.data.company?.currency || data.currency,
          point: response.data.company?.point ?? data.point,
          logo: response.data.company?.company_logo || data.logo,
          package: response.data.company?.is_trial_active ? "Trial" : (response.data.company?.package || data.package),
          is_trial_active: response.data.company?.is_trial_active || false,
        };
        setData(updatedCompany);
        updateCompany(updatedCompany);
        Swal.fire({
          icon: "success", title: "Success!",
          text: "Company settings updated successfully!",
          timer: 2000, showConfirmButton: false,
        });
      } else {
        throw new Error(response.data?.message || "Update failed");
      }
    } catch (error) {
      console.error("Update error:", error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        Swal.fire({ icon: "error", title: "Error", text: "Please fix the highlighted errors." });
      } else {
        Swal.fire({
          icon: "error", title: "Error",
          text: error.message || "Failed to update settings. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const currentCurrency = currencyList.find(c => c.code === data?.currency);
  const currencySymbol = currentCurrency?.symbol || "৳";

  if (!data) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Company Settings" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
      </Box>
    );
  }

  const handleAddWorkstation = async () => {
    if (!wsForm.name) return;
    try {
      await AxiosAuthInstance.post("workstations", wsForm);
      setWsForm({ name: "", code: "", type: "office", address: "" });
      const res = await AxiosAuthInstance.get("workstations");
      setWorkstations(res.data);
    } catch (err) { Swal.fire("Error", "Failed to add", "error"); }
  };

  const handleDeleteWorkstation = async (id) => {
    if (window.confirm("Delete this workstation?")) {
      await AxiosAuthInstance.delete(`workstations/${id}`);
      setWorkstations(workstations.filter(w => w.id !== id));
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <SectionCard>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2", mr: 2 }}><FaBuilding /></Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Company Information</Typography>
                    <Typography variant="body2" color="text.secondary">Basic details about your company</Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Company Name *" value={data.name || ""}
                      onChange={(e) => setData({ ...data, name: e.target.value })}
                      error={!!errors.company_name} helperText={errors.company_name?.[0]}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><FaBuilding style={{ color: "#757575" }} /></InputAdornment>) }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Email *" type="email" value={data.email || ""}
                      onChange={(e) => setData({ ...data, email: e.target.value })}
                      error={!!errors.company_email} helperText={errors.company_email?.[0]}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><FaEnvelope style={{ color: "#757575" }} /></InputAdornment>) }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Phone *" value={data.phone || ""}
                      onChange={(e) => setData({ ...data, phone: e.target.value })}
                      error={!!errors.company_phone} helperText={errors.company_phone?.[0]}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><FaPhone style={{ color: "#757575" }} /></InputAdornment>) }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Address *" multiline rows={3} value={data.address || ""}
                      onChange={(e) => setData({ ...data, address: e.target.value })}
                      error={!!errors.company_address} helperText={errors.company_address?.[0]}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><FaMapMarkerAlt style={{ color: "#757575" }} /></InputAdornment>) }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Mushak Number" value={data.mushak_number || ""}
                      onChange={(e) => setData({ ...data, mushak_number: e.target.value })}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><FaIdCard style={{ color: "#757575" }} /></InputAdornment>) }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="VAT (%)" type="number" value={data.vat ?? ""}
                      onChange={(e) => setData({ ...data, vat: e.target.value })}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><FaPercent style={{ color: "#757575" }} /></InputAdornment>), endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Currency *</InputLabel>
                      <Select value={data.currency || "BDT"} onChange={(e) => setData({ ...data, currency: e.target.value })} label="Currency *">
                        {currencyList.map((c) => (<MenuItem key={c.code} value={c.code}>{c.code} ({c.symbol})</MenuItem>))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
            <Grid item xs={12} lg={4}>
              <SectionCard>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", mr: 2 }}><FaImage /></Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Company Logo</Typography>
                    <Typography variant="body2" color="text.secondary">Upload your company logo</Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <LogoUploadArea onClick={() => fileInputRef.current?.click()}>
                  <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*,.svg" style={{ display: "none" }} />
                  <FaUpload size={32} style={{ color: "#667eea", marginBottom: 12 }} />
                  <Typography variant="body1" fontWeight={600} gutterBottom>Click to upload logo</Typography>
                  <Typography variant="caption" color="text.secondary">Max size: 2MB | JPG, PNG, GIF, SVG</Typography>
                </LogoUploadArea>
                {previewUrl && (
                  <Box sx={{ textAlign: "center", mt: 3 }}>
                    <img src={previewUrl} alt="Logo Preview" style={{ maxWidth: "150px", maxHeight: "150px", objectFit: "contain", borderRadius: 8, border: "2px solid #e0e0e0", padding: 8 }} />
                  </Box>
                )}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ bgcolor: "#f8f9ff", p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    <FaCheckCircle style={{ marginRight: 6, color: "#4caf50" }} />Live Preview
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Company:</strong> {data.name || "Your Company"}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Email:</strong> {data.email || "N/A"}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Currency:</strong> {data.currency} ({currencySymbol})</Typography>
                  <Typography variant="body2"><strong>VAT:</strong> {data.vat ?? 0}%</Typography>
                </Box>
              </SectionCard>
            </Grid>
          </Grid>
        );

      case "salary":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", mr: 2 }}><FaMoneyBillWave /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Salary Breakdown Rules</Typography>
                <Typography variant="body2" color="text.secondary">Configure salary components as percentage of basic</Typography>
              </Box>
            </Box>
            <Alert severity="info" sx={{ mb: 3 }}>💡 Set percentages for each component. Leave as 0 to disable.</Alert>
            <Grid container spacing={3}>
              {[
                { key: "basic_percent", label: "Basic Salary %" },
                { key: "house_rent_percent", label: "House Rent %" },
                { key: "medical_percent", label: "Medical Allowance %" },
                { key: "conveyance_percent", label: "Conveyance %" },
                { key: "food_allowance_percent", label: "Food Allowance %" },
                { key: "transport_allowance_percent", label: "Transport Allowance %" },
                { key: "mobile_allowance_percent", label: "Mobile Allowance %" },
              ].map((field) => (
                <Grid item xs={12} md={6} lg={4} key={field.key}>
                  <TextField fullWidth label={field.label} type="number"
                    value={businessRules.salary_rules?.[field.key] ?? 0}
                    onChange={(e) => handleNestedChange("salary_rules", field.key, Number(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    inputProps={{ min: 0, max: 100 }} />
                </Grid>
              ))}
              <Grid item xs={12} md={6} lg={4}>
                <TextField fullWidth label="Overtime Rate (Multiplier)" type="number"
                  value={businessRules.salary_rules?.overtime_rate ?? 0}
                  onChange={(e) => handleNestedChange("salary_rules", "overtime_rate", Number(e.target.value))}
                  inputProps={{ min: 0, max: 5, step: 0.1 }} helperText="e.g., 2 = 2x hourly rate" />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField fullWidth label="Working Days/Month" type="number"
                  value={businessRules.salary_rules?.working_days_per_month ?? 0}
                  onChange={(e) => handleNestedChange("salary_rules", "working_days_per_month", Number(e.target.value))}
                  inputProps={{ min: 0, max: 31 }} />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField fullWidth label="Daily Working Hours" type="number"
                  value={businessRules.salary_rules?.daily_working_hours ?? 0}
                  onChange={(e) => handleNestedChange("salary_rules", "daily_working_hours", Number(e.target.value))}
                  inputProps={{ min: 0, max: 24 }} />
              </Grid>
            </Grid>
          </SectionCard>
        );

      case "attendance":
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionCard>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar sx={{ bgcolor: "#fff3e0", color: "#ef6c00", mr: 2 }}><FaClock /></Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Office Hours</Typography>
                    <Typography variant="body2" color="text.secondary">Set your office working hours</Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Office Start Time" type="time"
                      value={businessRules.office_start_time || ""}
                      onChange={(e) => handleSimpleChange("office_start_time", e.target.value)}
                      InputLabelProps={{ shrink: true }} helperText="Leave empty to disable" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Office End Time" type="time"
                      value={businessRules.office_end_time || ""}
                      onChange={(e) => handleSimpleChange("office_end_time", e.target.value)}
                      InputLabelProps={{ shrink: true }} helperText="Leave empty to disable" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Grace Period (minutes)" type="number"
                      value={businessRules.grace_period_minutes ?? 0}
                      onChange={(e) => handleSimpleChange("grace_period_minutes", Number(e.target.value))}
                      inputProps={{ min: 0, max: 1440 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Late Penalty (minutes)" type="number"
                      value={businessRules.late_penalty_minutes ?? 0}
                      onChange={(e) => handleSimpleChange("late_penalty_minutes", Number(e.target.value))}
                      inputProps={{ min: 0, max: 1440 }} />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
            <Grid item xs={12}>
              <SectionCard>
                <Typography variant="h6" fontWeight={600} gutterBottom>Attendance Restrictions</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ToggleCard active={businessRules.enable_gps_attendance}>
                      <FormControlLabel control={<Switch checked={businessRules.enable_gps_attendance}
                        onChange={(e) => handleSimpleChange("enable_gps_attendance", e.target.checked)} color="primary" />}
                        label="Enable GPS Attendance" />
                    </ToggleCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="GPS Radius (KM)" type="number"
                      value={businessRules.gps_radius_km ?? 0}
                      onChange={(e) => handleSimpleChange("gps_radius_km", Number(e.target.value))}
                      disabled={!businessRules.enable_gps_attendance} inputProps={{ min: 0, max: 100, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleCard active={businessRules.enable_self_attendance}>
                      <FormControlLabel control={<Switch checked={businessRules.enable_self_attendance}
                        onChange={(e) => handleSimpleChange("enable_self_attendance", e.target.checked)} color="primary" />}
                        label="Enable Self Attendance" />
                    </ToggleCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleCard active={businessRules.enable_ip_restriction}>
                      <FormControlLabel control={<Switch checked={businessRules.enable_ip_restriction}
                        onChange={(e) => handleSimpleChange("enable_ip_restriction", e.target.checked)} color="primary" />}
                        label="Enable IP Restriction" />
                    </ToggleCard>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Allowed IPs (comma separated)"
                      value={businessRules.allowed_ips || ""}
                      onChange={(e) => handleSimpleChange("allowed_ips", e.target.value)}
                      disabled={!businessRules.enable_ip_restriction} placeholder="192.168.1.1, 192.168.1.2" />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
          </Grid>
        );

      case "leave":
        return (
          <Grid container spacing={3}>
            {/* ✅ Leave Policies (Casual, Sick, etc.) মুছে ফেলা হয়েছে, শুধু Settings রাখা হয়েছে */}
            <Grid item xs={12}>
              <SectionCard>
                <Typography variant="h6" fontWeight={600} gutterBottom>Leave Settings</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ToggleCard active={businessRules.leave_encashment}>
                      <FormControlLabel control={<Switch checked={businessRules.leave_encashment}
                        onChange={(e) => handleSimpleChange("leave_encashment", e.target.checked)} color="primary" />}
                        label="Enable Leave Encashment" />
                    </ToggleCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Max Carry Forward Days" type="number"
                      value={businessRules.max_leave_carry_forward ?? 0}
                      onChange={(e) => handleSimpleChange("max_leave_carry_forward", Number(e.target.value))}
                      inputProps={{ min: 0, max: 365 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleCard active={businessRules.leave_approval_required}>
                      <FormControlLabel control={<Switch checked={businessRules.leave_approval_required}
                        onChange={(e) => handleSimpleChange("leave_approval_required", e.target.checked)} color="primary" />}
                        label="Require Leave Approval" />
                    </ToggleCard>
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
          </Grid>
        );

      case "financial":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#ffebee", color: "#c62828", mr: 2 }}><FaChartLine /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Financial Rules</Typography>
                <Typography variant="body2" color="text.secondary">Configure tax, advance salary, and loan settings</Typography>
              </Box>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Tax Rate (%)" type="number"
                  value={businessRules.tax_rate ?? 0}
                  onChange={(e) => handleSimpleChange("tax_rate", Number(e.target.value))}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_advance_salary}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_advance_salary}
                    onChange={(e) => handleSimpleChange("enable_advance_salary", e.target.checked)} color="primary" />}
                    label="Enable Advance Salary" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Max Advance Percentage" type="number"
                  value={businessRules.max_advance_percentage ?? 0}
                  onChange={(e) => handleSimpleChange("max_advance_percentage", Number(e.target.value))}
                  disabled={!businessRules.enable_advance_salary} inputProps={{ min: 0, max: 100 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_loan}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_loan}
                    onChange={(e) => handleSimpleChange("enable_loan", e.target.checked)} color="primary" />}
                    label="Enable Employee Loan" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Max Loan Amount" type="number"
                  value={businessRules.max_loan_amount ?? 0}
                  onChange={(e) => handleSimpleChange("max_loan_amount", Number(e.target.value))}
                  disabled={!businessRules.enable_loan} inputProps={{ min: 0 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }} />
              </Grid>
            </Grid>
          </SectionCard>
        );

      case "inventory":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#efebe9", color: "#5d4037", mr: 2 }}><FaBox /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Inventory Management Rules</Typography>
                <Typography variant="body2" color="text.secondary">Configure stock and inventory settings</Typography>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_negative_stock}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_negative_stock}
                    onChange={(e) => handleSimpleChange("enable_negative_stock", e.target.checked)} color="primary" />}
                    label="Allow Negative Stock" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.auto_stock_alert}>
                  <FormControlLabel control={<Switch checked={businessRules.auto_stock_alert}
                    onChange={(e) => handleSimpleChange("auto_stock_alert", e.target.checked)} color="primary" />}
                    label="Enable Auto Stock Alerts" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Low Stock Threshold" type="number"
                  value={businessRules.low_stock_threshold ?? 0}
                  onChange={(e) => handleSimpleChange("low_stock_threshold", Number(e.target.value))}
                  disabled={!businessRules.auto_stock_alert} inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_batch_tracking}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_batch_tracking}
                    onChange={(e) => handleSimpleChange("enable_batch_tracking", e.target.checked)} color="primary" />}
                    label="Enable Batch Tracking" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_expiry_tracking}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_expiry_tracking}
                    onChange={(e) => handleSimpleChange("enable_expiry_tracking", e.target.checked)} color="primary" />}
                    label="Enable Expiry Tracking" />
                </ToggleCard>
              </Grid>
            </Grid>
          </SectionCard>
        );

      case "billing":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#eceff1", color: "#455a64", mr: 2 }}><FaFileInvoice /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Billing & Invoice Settings</Typography>
                <Typography variant="body2" color="text.secondary">Configure invoice numbering and billing rules</Typography>
              </Box>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_invoice_numbering}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_invoice_numbering}
                    onChange={(e) => handleSimpleChange("enable_invoice_numbering", e.target.checked)} color="primary" />}
                    label="Enable Invoice Numbering" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Invoice Prefix" value={businessRules.invoice_prefix || ""}
                  onChange={(e) => handleSimpleChange("invoice_prefix", e.target.value)}
                  disabled={!businessRules.enable_invoice_numbering} placeholder="INV" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Invoice Number Length" type="number"
                  value={businessRules.invoice_digit_length ?? 0}
                  onChange={(e) => handleSimpleChange("invoice_digit_length", Number(e.target.value))}
                  disabled={!businessRules.enable_invoice_numbering} inputProps={{ min: 0, max: 10 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.auto_send_invoice_email}>
                  <FormControlLabel control={<Switch checked={businessRules.auto_send_invoice_email}
                    onChange={(e) => handleSimpleChange("auto_send_invoice_email", e.target.checked)} color="primary" />}
                    label="Auto-send Invoice via Email" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_discount}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_discount}
                    onChange={(e) => handleSimpleChange("enable_discount", e.target.checked)} color="primary" />}
                    label="Enable Discount" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Max Discount Percentage" type="number"
                  value={businessRules.max_discount_percentage ?? 0}
                  onChange={(e) => handleSimpleChange("max_discount_percentage", Number(e.target.value))}
                  disabled={!businessRules.enable_discount} inputProps={{ min: 0, max: 100 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_tax_in_invoice}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_tax_in_invoice}
                    onChange={(e) => handleSimpleChange("enable_tax_in_invoice", e.target.checked)} color="primary" />}
                    label="Enable Tax in Invoice" />
                </ToggleCard>
              </Grid>
            </Grid>
          </SectionCard>
        );

      case "customer":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e0f7fa", color: "#00838f", mr: 2 }}><FaUserShield /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Customer Management Rules</Typography>
                <Typography variant="body2" color="text.secondary">Configure credit and loyalty settings</Typography>
              </Box>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_customer_credit}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_customer_credit}
                    onChange={(e) => handleSimpleChange("enable_customer_credit", e.target.checked)} color="primary" />}
                    label="Enable Customer Credit" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Max Credit Limit" type="number"
                  value={businessRules.max_credit_limit ?? 0}
                  onChange={(e) => handleSimpleChange("max_credit_limit", Number(e.target.value))}
                  disabled={!businessRules.enable_customer_credit} inputProps={{ min: 0 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Credit Days" type="number"
                  value={businessRules.credit_days ?? 0}
                  onChange={(e) => handleSimpleChange("credit_days", Number(e.target.value))}
                  disabled={!businessRules.enable_customer_credit} inputProps={{ min: 0, max: 365 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_loyalty_points}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_loyalty_points}
                    onChange={(e) => handleSimpleChange("enable_loyalty_points", e.target.checked)} color="primary" />}
                    label="Enable Loyalty Points" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Loyalty Point Percentage" type="number"
                  value={businessRules.loyalty_point_percentage ?? 0}
                  onChange={(e) => handleSimpleChange("loyalty_point_percentage", Number(e.target.value))}
                  disabled={!businessRules.enable_loyalty_points} inputProps={{ min: 0, max: 100, step: 0.1 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
            </Grid>
          </SectionCard>
        );

      case "approval":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#fbe9e7", color: "#d84315", mr: 2 }}><FaClipboardList /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Approval Workflow</Typography>
                <Typography variant="body2" color="text.secondary">Configure approval hierarchy levels</Typography>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Approval Levels" type="number"
                  value={businessRules.approval_levels ?? 0}
                  onChange={(e) => handleSimpleChange("approval_levels", Number(e.target.value))}
                  inputProps={{ min: 0, max: 5 }} helperText="Number of approval levels required" />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 3 }}>💡 Configure approval hierarchy in the backend admin panel.</Alert>
          </SectionCard>
        );

      case "notification":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#fce4ec", color: "#c2185b", mr: 2 }}><FaBell /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Notification Settings</Typography>
                <Typography variant="body2" color="text.secondary">Configure notification channels and alerts</Typography>
              </Box>
            </Box>
            <Grid container spacing={2}>
              {[
                { key: "enable_email_notifications", label: "Email Notifications", icon: <FaEnvelope /> },
                { key: "enable_sms_notifications", label: "SMS Notifications", icon: <FaPhone /> },
                { key: "enable_push_notifications", label: "Push Notifications", icon: <FaBell /> },
                { key: "birthday_notification", label: "Birthday Notifications", icon: <FaGift /> },
                { key: "attendance_alert", label: "Attendance Alerts", icon: <FaClock /> },
                { key: "stock_alert", label: "Stock Alerts", icon: <FaBox /> },
              ].map((notif) => (
                <Grid item xs={12} md={6} key={notif.key}>
                  <ToggleCard active={businessRules[notif.key]}>
                    <FormControlLabel control={<Switch checked={businessRules[notif.key]}
                      onChange={(e) => handleSimpleChange(notif.key, e.target.checked)} color="primary" />}
                      label={<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{notif.icon}<span>{notif.label}</span></Box>} />
                  </ToggleCard>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        );

      case "security":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e8eaf6", color: "#283593", mr: 2 }}><FaShieldAlt /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Security Settings</Typography>
                <Typography variant="body2" color="text.secondary">Configure security and authentication settings</Typography>
              </Box>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Password Expiry (Days)" type="number"
                  value={businessRules.password_expiry_days ?? 0}
                  onChange={(e) => handleSimpleChange("password_expiry_days", Number(e.target.value))}
                  inputProps={{ min: 0, max: 365 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_two_factor}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_two_factor}
                    onChange={(e) => handleSimpleChange("enable_two_factor", e.target.checked)} color="primary" />}
                    label="Enable Two-Factor Authentication" />
                </ToggleCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Session Timeout (Minutes)" type="number"
                  value={businessRules.session_timeout_minutes ?? 0}
                  onChange={(e) => handleSimpleChange("session_timeout_minutes", Number(e.target.value))}
                  inputProps={{ min: 0, max: 480 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard active={businessRules.enable_ip_whitelist}>
                  <FormControlLabel control={<Switch checked={businessRules.enable_ip_whitelist}
                    onChange={(e) => handleSimpleChange("enable_ip_whitelist", e.target.checked)} color="primary" />}
                    label="Enable IP Whitelist" />
                </ToggleCard>
              </Grid>
            </Grid>
          </SectionCard>
        );

      case "branding":
        return (
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#f1f8e9", color: "#558b2f", mr: 2 }}><FaPalette /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Branding & Communication</Typography>
                <Typography variant="body2" color="text.secondary">Customize your brand messaging</Typography>
              </Box>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField fullWidth label="Company Tagline" value={businessRules.company_tagline || ""}
                  onChange={(e) => handleSimpleChange("company_tagline", e.target.value)}
                  placeholder="Your company tagline" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Footer Text (for Invoices)" multiline rows={2}
                  value={businessRules.footer_text || ""}
                  onChange={(e) => handleSimpleChange("footer_text", e.target.value)}
                  placeholder="Thank you for your business!"
                  helperText="Appears at the bottom of invoices and receipts" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Terms & Conditions" multiline rows={6}
                  value={businessRules.terms_conditions || ""}
                  onChange={(e) => handleSimpleChange("terms_conditions", e.target.value)}
                  placeholder="Enter your terms and conditions..." />
              </Grid>
            </Grid>
          </SectionCard>
        );

      case "employee":
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionCard>
                <Typography variant="h6" fontWeight={600} gutterBottom>💰 Salary Increment Rules</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} lg={3}>
                    <TextField fullWidth label="Annual Increment %" type="number"
                      value={businessRules.employee_increment_rules?.annual_percent ?? 10}
                      onChange={(e) => handleNestedChange("employee_increment_rules", "annual_percent", Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <TextField fullWidth label="Performance Bonus Max %" type="number"
                      value={businessRules.employee_increment_rules?.performance_max ?? 20}
                      onChange={(e) => handleNestedChange("employee_increment_rules", "performance_max", Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <TextField fullWidth label="Tenure Bonus %" type="number"
                      value={businessRules.employee_increment_rules?.tenure_bonus ?? 5}
                      onChange={(e) => handleNestedChange("employee_increment_rules", "tenure_bonus", Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <TextField fullWidth label="Min Service (Months)" type="number"
                      value={businessRules.employee_increment_rules?.min_service_months ?? 12}
                      onChange={(e) => handleNestedChange("employee_increment_rules", "min_service_months", Number(e.target.value))}
                      inputProps={{ min: 0, max: 240 }} />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
            <Grid item xs={12}>
              <SectionCard>
                <Typography variant="h6" fontWeight={600} gutterBottom>🎓 Probation Rules</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Probation Duration (Months)" type="number"
                      value={businessRules.probation_rules?.duration_months ?? 3}
                      onChange={(e) => handleNestedChange("probation_rules", "duration_months", Number(e.target.value))}
                      inputProps={{ min: 1, max: 24 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Confirmation Increment %" type="number"
                      value={businessRules.probation_rules?.confirmation_increment ?? 5}
                      onChange={(e) => handleNestedChange("probation_rules", "confirmation_increment", Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
          </Grid>
        );

      case "separation":
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionCard>
                <Typography variant="h6" fontWeight={600} gutterBottom>🔴 Termination Rules</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Notice Period (Days)" type="number"
                      value={businessRules.termination_rules?.notice_days ?? 30}
                      onChange={(e) => handleNestedChange("termination_rules", "notice_days", Number(e.target.value))}
                      inputProps={{ min: 0, max: 365 }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Severance Pay (Months)" type="number"
                      value={businessRules.termination_rules?.severance_months ?? 1}
                      onChange={(e) => handleNestedChange("termination_rules", "severance_months", Number(e.target.value))}
                      inputProps={{ min: 0, max: 24, step: 0.5 }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Max Years for Severance" type="number"
                      value={businessRules.termination_rules?.max_years_for_severance ?? 10}
                      onChange={(e) => handleNestedChange("termination_rules", "max_years_for_severance", Number(e.target.value))}
                      inputProps={{ min: 0, max: 50 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleCard active={businessRules.termination_rules?.leave_encashment ?? true}>
                      <FormControlLabel control={<Switch checked={businessRules.termination_rules?.leave_encashment ?? true}
                        onChange={(e) => handleNestedChange("termination_rules", "leave_encashment", e.target.checked)} color="primary" />}
                        label="Leave Encashment on Termination" />
                    </ToggleCard>
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
            <Grid item xs={12}>
              <SectionCard>
                <Typography variant="h6" fontWeight={600} gutterBottom>🟡 Resignation Rules</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Notice Period (Days)" type="number"
                      value={businessRules.resignation_rules?.notice_days ?? 30}
                      onChange={(e) => handleNestedChange("resignation_rules", "notice_days", Number(e.target.value))}
                      inputProps={{ min: 0, max: 365 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleCard active={businessRules.resignation_rules?.leave_encashment ?? true}>
                      <FormControlLabel control={<Switch checked={businessRules.resignation_rules?.leave_encashment ?? true}
                        onChange={(e) => handleNestedChange("resignation_rules", "leave_encashment", e.target.checked)} color="primary" />}
                        label="Leave Encashment on Resignation" />
                    </ToggleCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleCard active={businessRules.resignation_rules?.bonus_prorated ?? true}>
                      <FormControlLabel control={<Switch checked={businessRules.resignation_rules?.bonus_prorated ?? true}
                        onChange={(e) => handleNestedChange("resignation_rules", "bonus_prorated", e.target.checked)} color="primary" />}
                        label="Prorate Bonus on Resignation" />
                    </ToggleCard>
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
          </Grid>
        );

      case "workstation":
        return (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2", mr: 2 }}><FaMapMarkerAlt /></Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Workstation / Office Locations</Typography>
                <Typography variant="body2" color="text.secondary">Manage company branches, head office, and factory locations</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <SectionCard sx={{ p: 2, bgcolor: "#f8f9ff" }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Add New Workstation</Typography>
                  <Stack spacing={2}>
                    <TextField size="small" label="Name *" value={wsForm.name} onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })} />
                    <TextField size="small" label="Code (e.g., HO, BO-1)" value={wsForm.code} onChange={(e) => setWsForm({ ...wsForm, code: e.target.value })} />
                    <FormControl size="small">
                      <InputLabel>Type</InputLabel>
                      <Select value={wsForm.type} onChange={(e) => setWsForm({ ...wsForm, type: e.target.value })} label="Type">
                        <MenuItem value="office">Head/Branch Office</MenuItem>
                        <MenuItem value="factory">Factory</MenuItem>
                        <MenuItem value="warehouse">Warehouse</MenuItem>
                        <MenuItem value="remote">Remote</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Address" multiline rows={2} value={wsForm.address} onChange={(e) => setWsForm({ ...wsForm, address: e.target.value })} />
                    <Button variant="contained" onClick={handleAddWorkstation} startIcon={<FaPlus />}>Add Workstation</Button>
                  </Stack>
                </SectionCard>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  {workstations.map((ws) => (
                    <Paper key={ws.id} sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #e0e0e0" }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{ws.name} <Chip label={ws.code} size="small" sx={{ ml: 1 }} /></Typography>
                        <Typography variant="caption" color="text.secondary">{ws.type.toUpperCase()} | {ws.address || "No address"}</Typography>
                      </Box>
                      <IconButton color="error" onClick={() => handleDeleteWorkstation(ws.id)}><FaTrash /></IconButton>
                    </Paper>
                  ))}
                  {workstations.length === 0 && <Typography color="text.secondary">No workstations added yet.</Typography>}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        );

      case "festivals":
        return <FestivalManagement />;

      case "employment":
        return <EmploymentTypeRules />;

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Company Settings" />
      <GradientPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, position: "relative", zIndex: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaCog style={{ marginRight: 10, verticalAlign: "middle" }} />Company Configuration
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>Manage your company settings and business logic</Typography>
          </Box>
          <Chip icon={<FaCrown />} label={`Package: ${data.package}`}
            sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, "& .MuiChip-icon": { color: "#ffd700" } }} />
        </Box>
      </GradientPaper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", position: "sticky", top: 80, maxHeight: "calc(100vh - 100px)", overflowY: "auto" }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ px: 2, mb: 2, color: "text.secondary" }}>SETTINGS</Typography>
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <TabButton key={tab.key} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} startIcon={<IconComponent />}>
                  {tab.label}
                </TabButton>
              );
            })}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          {renderTabContent()}
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={() => window.location.reload()} startIcon={<FaTimes />}
              sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2 }}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FaSave />}
              sx={{
                textTransform: "none", fontWeight: 600, px: 4, borderRadius: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": { background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)", boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)" }
              }}>
              {loading ? "Saving..." : "Save All Settings"}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanySetting;